import React, { useState, useEffect, useCallback } from 'react'
import { Table, Button, Modal, Form, Input, Space, Typography, Tag, Switch, message, Spin, Tooltip, Select } from 'antd'
import { PlusOutlined, EditOutlined, CheckCircleOutlined, StopOutlined, ReloadOutlined } from '@ant-design/icons'
import { paymentMethodsService, type PaymentMethodItem } from '../../../shared/api/services/payment-methods.service'

const { Title, Text } = Typography

export const PaymentMethodSettings: React.FC = () => {
    const [loading, setLoading] = useState(false)
    const [methods, setMethods] = useState<PaymentMethodItem[]>([])
    const [isModalVisible, setIsModalVisible] = useState(false)
    const [editingMethod, setEditingMethod] = useState<PaymentMethodItem | null>(null)
    const [saving, setSaving] = useState(false)
    const [form] = Form.useForm()

    const fetchMethods = useCallback(async () => {
        setLoading(true)
        try {
            const data = await paymentMethodsService.getPaymentMethods()
            setMethods(data)
        } catch (error: any) {
            message.error(error.message || 'Failed to load payment methods')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchMethods()
    }, [fetchMethods])

    const showModal = (method?: PaymentMethodItem) => {
        setEditingMethod(method || null)
        if (method) {
            form.setFieldsValue({
                ...method,
                status: method.status || 'active',
                is_default: !!method.is_default,
            })
        } else {
            form.resetFields()
            form.setFieldsValue({
                status: 'active',
                is_default: false,
                sort_order: (methods.length + 1) * 10,
            })
        }
        setIsModalVisible(true)
    }

    const handleCancel = () => {
        setIsModalVisible(false)
        setEditingMethod(null)
    }

    const onFinish = async (values: any) => {
        setSaving(true)
        try {
            if (editingMethod) {
                await paymentMethodsService.updatePaymentMethod(editingMethod.id, {
                    name: values.name,
                    description: values.description,
                    icon_name: values.icon_name,
                    status: values.status,
                    is_default: values.status === 'inactive' ? false : !!values.is_default,
                    sort_order: values.sort_order ? Number(values.sort_order) : undefined,
                })
                message.success('Payment method updated successfully')
            } else {
                await paymentMethodsService.createPaymentMethod({
                    code: values.code.toUpperCase().trim(),
                    name: values.name.trim(),
                    description: values.description,
                    icon_name: values.icon_name,
                    status: values.status,
                    is_default: values.status === 'inactive' ? false : !!values.is_default,
                    sort_order: values.sort_order ? Number(values.sort_order) : 0,
                })
                message.success('Payment method created successfully')
            }
            setIsModalVisible(false)
            setEditingMethod(null)
            await fetchMethods()
        } catch (error: any) {
            message.error(error.response?.data?.message || error.message || 'Failed to save payment method')
        } finally {
            setSaving(false)
        }
    }

    const handleToggleStatus = async (record: PaymentMethodItem) => {
        const nextStatus = record.status === 'active' ? 'inactive' : 'active'
        const actionLabel = nextStatus === 'active' ? 'activate' : 'deactivate'

        Modal.confirm({
            title: `Are you sure you want to ${actionLabel} "${record.name}"?`,
            content: nextStatus === 'inactive' 
                ? 'This payment method will no longer be available for new RFQ bidding or merchant selection.'
                : 'This payment method will become available on the platform.',
            okText: `Yes, ${actionLabel}`,
            okType: nextStatus === 'inactive' ? 'danger' : 'primary',
            onOk: async () => {
                try {
                    await paymentMethodsService.updateStatus(record.id, nextStatus)
                    message.success(`Payment method set to ${nextStatus}`)
                    await fetchMethods()
                } catch (error: any) {
                    message.error(error.response?.data?.message || error.message || `Failed to ${actionLabel} payment method`)
                }
            }
        })
    }

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: PaymentMethodItem) => (
                <Space direction="vertical" size={2}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{text}</span>
                    {record.icon_name && (
                        <Text type="secondary" style={{ fontSize: 12 }}>Icon: {record.icon_name}</Text>
                    )}
                </Space>
            )
        },
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            render: (code: string) => <Tag color="blue" style={{ fontWeight: 600 }}>{code}</Tag>
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (desc: string) => desc || <Text type="secondary">—</Text>
        },
        {
            title: 'Default on Signup',
            dataIndex: 'is_default',
            key: 'is_default',
            render: (isDefault: boolean) => (
                <Tag color={isDefault ? 'purple' : 'default'} style={{ fontWeight: 600 }}>
                    {isDefault ? '★ DEFAULT' : 'OPTIONAL'}
                </Tag>
            )
        },
        {
            title: 'Platform Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string, record: PaymentMethodItem) => (
                <Tooltip title="Click to toggle status">
                    <Tag 
                        color={status === 'active' ? 'green' : 'error'}
                        style={{ cursor: 'pointer', fontWeight: 600 }}
                        onClick={() => handleToggleStatus(record)}
                    >
                        {status === 'active' ? <CheckCircleOutlined /> : <StopOutlined />} {status.toUpperCase()}
                    </Tag>
                </Tooltip>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: PaymentMethodItem) => (
                <Space>
                    <Button 
                        icon={<EditOutlined />} 
                        onClick={() => showModal(record)}
                        type="text"
                        title="Edit Details"
                    >
                        Edit
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        danger={record.status === 'active'}
                        onClick={() => handleToggleStatus(record)}
                    >
                        {record.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                </Space>
            )
        }
    ]

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                    <Title level={4} style={{ margin: 0 }}>Payment Method Master</Title>
                    <Text type="secondary">Manage accepted payment methods across the marketplace and configure defaults for new merchant signups.</Text>
                </div>
                <Space>
                    <Button 
                        icon={<ReloadOutlined />} 
                        onClick={() => fetchMethods()}
                        loading={loading}
                    >
                        Refresh
                    </Button>
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />} 
                        onClick={() => showModal()}
                    >
                        Add Payment Method
                    </Button>
                </Space>
            </div>

            <Table 
                columns={columns} 
                dataSource={methods} 
                rowKey="id"
                loading={loading}
                pagination={false}
            />

            <Modal
                title={editingMethod ? `Edit Payment Method: ${editingMethod.name}` : 'Add Payment Method'}
                open={isModalVisible}
                onCancel={handleCancel}
                onOk={() => form.submit()}
                confirmLoading={saving}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                >
                    <Form.Item 
                        label="Short Code" 
                        name="code" 
                        rules={[
                            { required: true, message: 'Please enter short code' },
                            { pattern: /^[A-Za-z0-9_]+$/, message: 'Code can only contain letters, numbers, and underscores' }
                        ]}
                    >
                        <Input 
                            placeholder="e.g. UPI_QR" 
                            disabled={!!editingMethod}
                            style={{ textTransform: 'uppercase' }}
                        />
                    </Form.Item>

                    <Form.Item 
                        label="Method Name" 
                        name="name" 
                        rules={[{ required: true, message: 'Please enter method name' }]}
                    >
                        <Input placeholder="e.g. UPI / QR Code" />
                    </Form.Item>

                    <Form.Item 
                        label="Icon Identifier" 
                        name="icon_name"
                        help="Lucide icon name (e.g. Smartphone, Truck, CreditCard, Package, Wallet)"
                    >
                        <Input placeholder="e.g. Smartphone" />
                    </Form.Item>

                    <Form.Item 
                        label="Description" 
                        name="description"
                    >
                        <Input.TextArea placeholder="Enter details about this payment method" rows={3} />
                    </Form.Item>

                    <Form.Item 
                        label="Platform Status" 
                        name="status"
                        rules={[{ required: true }]}
                    >
                        <Select>
                            <Select.Option value="active">Active (Available on platform)</Select.Option>
                            <Select.Option value="inactive">Inactive (Disabled on platform)</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item 
                        label="Default for New Merchants" 
                        name="is_default" 
                        valuePropName="checked"
                        help="When enabled, newly registered merchants will have this payment method turned ON automatically."
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item 
                        label="Sort Order" 
                        name="sort_order"
                    >
                        <Input type="number" placeholder="e.g. 10" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    )
}
